import { fetchSound } from '../../infrastructure/content/soundClient';
import { readSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * The one place the duel makes a noise (SPEC-007 PR-7.6, D-71).
 *
 * Named for the job rather than for its first job: it was `duelTick.js` while
 * the clock was the only thing here, and a module that also plays the sound of
 * winning a round cannot keep that name and stay honest.
 *
 * The clock is already drawn twice — a digit and a drain — and both of them ask
 * the reader to be LOOKING at it in the seconds when they are looking at two
 * cars. A tick is the only reading of the clock that arrives while the eyes are
 * somewhere else, which is exactly what the last ten seconds are wanted for.
 *
 * TWO VOICES OVER ONE RAMP. From ten seconds a soft alternating tick-tock; from
 * five the harder stroke the heat ramp arrives with. Both climb in speed and
 * gain with `duelClock.tickPressure`, so the round tightens continuously and the
 * change of character at five lands on the same second as the change of colour.
 *
 * RECORDED, NOT SYNTHESISED, and that reverses an earlier decision in this file.
 * The first cut was two oscillators and a gain envelope, on the argument that a
 * `tick.mp3` is an asset, a fetch and a second thing the service worker has to
 * be right about — for forty milliseconds of sound. The argument was sound and
 * the conclusion was wrong: a square wave is a beep, and a listener files a beep
 * under "interface", where a recording of an escapement is a CLOCK. What made
 * the trade work in the end was cutting the delivery down to size —
 * `scripts/build-sound-assets.js` takes one stroke out of each nine-second
 * recording on MPEG frame boundaries — so what ships is about fifteen kilobytes
 * rather than seven hundred, fetched only when a reader first reaches ten
 * seconds with the sound on.
 *
 * DECODED ONCE AND KEPT. An `AudioBuffer` is reusable and decoding is not free,
 * so both slices are decoded on the same gesture that warms the context, and
 * every tick after that is a buffer source over them. A tick owed before the
 * bytes have arrived is SILENCE rather than a queue: this is a reading of a
 * clock the screen is already drawing twice, and a tick played late is worse
 * than one not played at all.
 *
 * PRIMED BY A GESTURE, NOT BY FIRST USE. iOS Safari creates an `AudioContext`
 * suspended and will not resume one outside a user gesture, and the first tick
 * of a round is ten seconds after any tap. So `primeDuelTick` is called from
 * inside the reader's own touch — the caller's business, not this module's — and
 * the context is warm and the buffers are in by the time the clock needs them.
 *
 * EVERY ENTRY POINT IS SAFE ON A PLATFORM THAT HAS NONE OF THIS, and a tick that
 * cannot be played is silence rather than an error. There is no state anywhere
 * in the game that depends on a sound having been made.
 *
 * `prefers-reduced-motion` is deliberately NOT consulted. It is a statement about
 * movement — vestibular discomfort, not a request for quiet — and a reader who
 * turned it on to stop cards flying at them still wants to hear their clock run
 * out. The mute preference below is the control for sound, and it is the only
 * one.
 */

/**
 * Every recording, by the name `build-sound-assets.js` writes it under.
 *
 * One flat map rather than a shape per use, because the only thing this level
 * needs to know is which bytes go with which name — and it is what lets a voice
 * name two of them without the loader learning about voices.
 */
const SLICES = {
    'tick-gentle': 'tick-gentle.mp3',
    'tock-gentle': 'tock-gentle.mp3',
    'tick-urgent': 'tick-urgent.mp3',
    win: 'win.mp3',
    lost: 'lost.mp3',
};

/**
 * THE TWO VOICES, and the second is not the first turned up.
 *
 * From ten seconds the clock keeps TIME — an old escapement, which a reader
 * hears as a room rather than as an alarm. From five it keeps SCORE: the
 * countdown recording, which has a harder attack.
 *
 * THE GENTLE VOICE HAS A REAL TICK AND A REAL TOCK. It was one slice played
 * slower on alternate seconds for a revision, which is a fair imitation of what
 * an escapement's two strokes differ by — but the recording HAS both, 0.31s
 * apart, so the imitation was standing in for something that was already there.
 * The urgent voice names the same slice twice on purpose: a countdown that kept
 * swinging between two pitches would be saying "steady" with the tone while
 * saying "hurry" with everything else.
 *
 * `offsetSeconds` is where playback STARTS inside each slice. Every cut opens a
 * few frames before its stroke, in the silence, so that the attack is not
 * clipped and Layer III's bit reservoir has somewhere harmless to warm up
 * (`scripts/lib/mp3Frames.js`); playing from the top would put that silence
 * between the second and the sound of it.
 *
 * `gain` is a NORMALISATION, and the two are a long way apart because the
 * recordings are: the escapement peaks at 0.644 and the countdown at 0.281.
 * Rather than re-encode a recording somebody else made, the level is set here,
 * where it also carries the pressure ramp.
 */
const VOICES = {
    gentle: {
        tick: 'tick-gentle',
        tock: 'tock-gentle',
        offsetSeconds: 0.05,
        gain: 0.42,
    },
    urgent: {
        tick: 'tick-urgent',
        tock: 'tick-urgent',
        offsetSeconds: 0.05,
        gain: 1.9,
    },
};

/**
 * The sound a round ends on.
 *
 * Not normalised as hard as the clock is: a stinger that arrives at the level of
 * an interface click IS an interface click, and these two are allowed to be the
 * loudest thing the product does.
 */
const OUTCOMES = {
    win: { slice: 'win', gain: 1.6 },
    lost: { slice: 'lost', gain: 1.1 },
};

/**
 * How far the pressure winds a voice up, as a share of its own speed.
 *
 * A recording cannot climb in pitch the way an oscillator could, so it climbs by
 * being played FASTER — which raises the pitch and shortens the stroke together,
 * and is what a wound spring does. Twelve per cent is audible across ten seconds
 * and stops well short of turning the last tick into a chirp.
 */
const PRESSURE_RATE = 0.12;

/**
 * How much of a voice's gain is already there at the top of its window, so the
 * build is a build rather than a fade-in from silence. At zero pressure a tick
 * still has to be audible.
 */
const PRESSURE_FLOOR = 0.6;

const SOUND_SETTING_KEY = 'duel:sound';

let audioContext = null;
/** The decoded slices, by name. Empty until a gesture has warmed them. */
const buffers = {};
/** In flight, so a second gesture does not start a second pair of fetches. */
let loading = null;

function getAudioContextConstructor() {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.AudioContext || window.webkitAudioContext || null;
}

function ensureAudioContext() {
    if (audioContext) {
        return audioContext;
    }

    const AudioContextConstructor = getAudioContextConstructor();

    if (!AudioContextConstructor) {
        return null;
    }

    try {
        audioContext = new AudioContextConstructor();
    } catch (error) {
        audioContext = null;
    }

    return audioContext;
}

/**
 * `decodeAudioData` in its promise form, with the callback form underneath.
 *
 * Safari implemented the callback signature first and still answers `undefined`
 * from the promise one on some versions, so a `.then` on the return value is not
 * something to rely on. Wrapping the callbacks covers both and costs a closure.
 */
function decode(context, bytes) {
    return new Promise((resolve, reject) => {
        try {
            const answered = context.decodeAudioData(bytes, resolve, reject);

            if (answered && typeof answered.then === 'function') {
                answered.then(resolve, reject);
            }
        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Whether this device wants the clock audible.
 *
 * ON unless the reader has said otherwise, and the stored value is the OPT-OUT
 * rather than the opt-in: a preference nobody has expressed reads as absent
 * here, and a feature that only works once you have found its switch is a
 * feature most readers never meet. The switch is on the scoreboard.
 */
export function isDuelSoundOn() {
    return readSetting(SOUND_SETTING_KEY) !== 'off';
}

export function setDuelSoundOn(isOn) {
    return writeSetting(SOUND_SETTING_KEY, isOn ? 'on' : 'off');
}

/**
 * Warm the context and pull the recordings in. Call from inside a real user
 * gesture; failure is silence.
 *
 * The fetches are fired and NOT awaited by the caller — priming happens on a
 * touch and a touch handler that waited on a network round trip would be a
 * touch that felt slow. Whichever of the two slices arrives first is playable
 * first, which is why they are stored one at a time rather than as a pair.
 */
export function primeDuelTick() {
    const context = ensureAudioContext();

    if (!context) {
        return;
    }

    if (context.state === 'suspended' && typeof context.resume === 'function') {
        context.resume().catch(() => {});
    }

    const wanted = Object.entries(SLICES).filter(([name]) => !buffers[name]);

    if (loading || wanted.length === 0) {
        return;
    }

    loading = Promise.all(wanted.map(async ([name, file]) => {
        try {
            buffers[name] = await decode(context, await fetchSound(file));
        } catch (error) {
            // A slice that will not arrive or will not decode is a sound this
            // duel does not have. The others may still be fine, and playing
            // none of them is a state this app is built to have.
            buffers[name] = null;
        }
    })).then(() => {
        loading = null;
    });
}

/**
 * One tick.
 *
 * `pressure` is `duelClock.tickPressure` — 0 at the top of the audible window
 * and 1 on the last second — and it drives both the gain and the playback rate,
 * so the clock builds continuously rather than in the nine steps a table would
 * have needed somebody to keep in step with the two constants in the domain.
 *
 * `isUrgent` picks the voice and `isTock` alternates the gentle one. Both are
 * the caller's to decide: which second it is is the scheduler's business, and
 * this module's is what a second sounds like.
 */
export function playDuelTick({ pressure = 1, isUrgent = true, isTock = false } = {}) {
    const context = ensureAudioContext();
    const voice = isUrgent ? VOICES.urgent : VOICES.gentle;
    const buffer = buffers[isTock ? voice.tock : voice.tick];

    if (!context || !buffer || typeof context.createBufferSource !== 'function') {
        return false;
    }

    try {
        if (context.state === 'suspended' && typeof context.resume === 'function') {
            context.resume().catch(() => {});
        }

        const climb = Math.min(1, Math.max(0, pressure));
        const source = context.createBufferSource();
        const gain = context.createGain();

        source.buffer = buffer;
        source.playbackRate.value = 1 + (PRESSURE_RATE * climb);
        gain.gain.value = voice.gain * (PRESSURE_FLOOR + ((1 - PRESSURE_FLOOR) * climb));

        source.connect(gain);
        gain.connect(context.destination);
        // From the offset to the end of the slice: the cut is already about the
        // length of one stroke, so there is nothing after it to stop before.
        source.start(context.currentTime, voice.offsetSeconds);

        return true;
    } catch (error) {
        // A browser that exposes the API and refuses the call. The clock is
        // still drawn twice; nothing the reader needs was in here.
        return false;
    }
}

/**
 * The sound a round ends on.
 *
 * One call for both endings, because they are one decision at the call site: the
 * round was won or it was not.
 *
 * Played at its own level with no pressure ramp and from the top of the slice: a
 * stinger is a full stop rather than a reading of something, there is nothing
 * about it that gets more urgent, and both cuts already open on their own
 * attack.
 */
export function playDuelOutcome({ isWin = true } = {}) {
    const context = ensureAudioContext();
    const outcome = isWin ? OUTCOMES.win : OUTCOMES.lost;
    const buffer = buffers[outcome.slice];

    if (!context || !buffer || typeof context.createBufferSource !== 'function') {
        return false;
    }

    try {
        if (context.state === 'suspended' && typeof context.resume === 'function') {
            context.resume().catch(() => {});
        }

        const source = context.createBufferSource();
        const gain = context.createGain();

        source.buffer = buffer;
        gain.gain.value = outcome.gain;

        source.connect(gain);
        gain.connect(context.destination);
        source.start(context.currentTime);

        return true;
    } catch (error) {
        return false;
    }
}
